import type { Board } from "../board";
import { isOnAddToBoard } from "../interfaces";
import type { PhysicalThing } from "../physicalThing";
import { Floor } from "../things/floors/floor";
import { MultiTileThingCore } from "../things/multitile/multiTileThingCore";
import { MultiTileThingPart } from "../things/multitile/multiTileThingPart";
import type { XY } from "../xy";
import { SimpleBoard } from "./simpleBoard";
import type { PrefabData } from "./prefabs/prefabData";

// Category-based preferred characters for human-readable prefab export.
// Things matching a category try these chars first (in order).
// All preferred chars are reserved exclusively for their categories.
const CATEGORY_PREFS: {
	match: (key: string) => boolean;
	chars: string[];
}[] = [
	{ match: (k) => k.includes("Floor") || k === "GraySlab", chars: [".", ",", "_"] },
	{ match: (k) => k.endsWith("Door"), chars: ["/", "+"] },
	{
		match: (k) => k === "MetalBlock" || k === "ConcreteBlock" || k === "MossBrick" || k === "Rock" || k === "CaveRock",
		chars: ["#", "%", "&", "X"],
	},
	{ match: (k) => k === "LootBox", chars: ["$"] },
	{ match: (k) => k === "Fence", chars: ["="] },
	{ match: (k) => k === "PottedPlant", chars: ["P"] },
	{ match: (k) => k === "LavaPool", chars: ["U"] },
	{ match: (k) => k === "Chasm", chars: ["V"] },
];

const RESERVED_CHARS = new Set<string>();
for (const cat of CATEGORY_PREFS) {
	for (const ch of cat.chars) RESERVED_CHARS.add(ch);
}

// General pool: A-Z, a-z, 0-9 minus reserved chars
const GENERAL_CHAR_POOL: string[] = [];
for (let c = 65; c <= 90; c++) GENERAL_CHAR_POOL.push(String.fromCharCode(c));
for (let c = 97; c <= 122; c++) GENERAL_CHAR_POOL.push(String.fromCharCode(c));
for (let c = 48; c <= 57; c++) GENERAL_CHAR_POOL.push(String.fromCharCode(c));
// Remove reserved category chars so they can't be accidentally assigned to unrelated things
const filteredPool = GENERAL_CHAR_POOL.filter((ch) => !RESERVED_CHARS.has(ch));

export class Prefab {
	readonly simpleBoard: SimpleBoard;
	cornerRequired: boolean = false;
	borderRequired: boolean = false;

	constructor(simpleBoard?: SimpleBoard) {
		this.simpleBoard = simpleBoard ?? new SimpleBoard();
	}

	fitsInPosition(board: Board, position: XY): boolean {
		for (const thing of this.simpleBoard.getThingList()) {
			const dest = position.add(thing.xy);
			if (!dest.isInside(board) || !board.isCellEmpty(dest)) return false;
		}
		return true;
	}

	fitsInPositionByCriteria(position: XY, condition: (xy: XY) => boolean): boolean {
		for (const thing of this.simpleBoard.getThingList()) {
			const dest = position.add(thing.xy);
			if (!condition(dest)) return false;
		}
		return true;
	}

	addToBoard(board: Board, position: XY): XY[] {
		const usedPositions: XY[] = [];
		const addedThings: PhysicalThing[] = [];
		for (const thing of this.simpleBoard.getThingList()) {
			const dest = position.add(thing.xy);
			(thing as any).xy = dest;
			// MultiTileThingCores must not occupy cells — parts handle physical
			// presence. This mirrors Board.deserialize which does the same check.
			if (thing instanceof MultiTileThingCore) {
				board.addNonPhysicalThing(thing, true);
			} else {
				board.addThing(thing as PhysicalThing, true);
				addedThings.push(thing);
			}
			usedPositions.push(dest);
		}
		// Fire onAddToBoard after all things are placed so neighbor checks see the full prefab
		for (const thing of addedThings) {
			if (isOnAddToBoard(thing)) {
				thing.onAddToBoard();
			}
		}
		for (const nonPhysical of this.simpleBoard.getNonPhysicalThings()) {
			board.addNonPhysicalThing(nonPhysical);
		}
		return usedPositions;
	}

	rotate(turns90: number): void {
		this.simpleBoard.rotate(turns90);
	}

	clone(): Prefab {
		const cloned = new Prefab(this.simpleBoard.clone());
		cloned.cornerRequired = this.cornerRequired;
		cloned.borderRequired = this.borderRequired;
		return cloned;
	}

	flipH(): void {
		this.simpleBoard.flipH();
	}

	flipV(): void {
		this.simpleBoard.flipV();
	}

	trim(): void {
		this.simpleBoard.trim();
	}

	exportToPrefabData(): PrefabData {
		const allThings = this.simpleBoard.getThingList();

		// Filter out MultiTileThingParts — initOnBoard creates those automatically
		const things = allThings.filter((t) => !(t instanceof MultiTileThingPart));

		if (things.length === 0) {
			return { legend: {}, layout: [] };
		}

		// Find bounds and shift so top-left is (0,0)
		let minX = Infinity,
			minY = Infinity,
			maxX = -Infinity,
			maxY = -Infinity;
		for (const t of things) {
			if (t.xy.x < minX) minX = t.xy.x;
			if (t.xy.y < minY) minY = t.xy.y;
			if (t.xy.x > maxX) maxX = t.xy.x;
			if (t.xy.y > maxY) maxY = t.xy.y;
		}

		const width = maxX - minX + 1;
		const height = maxY - minY + 1;

		// Group things by cell (relative coordinates), sorted by z-order
		const cellKey = (x: number, y: number) => `${x},${y}`;
		const cellMap = new Map<string, PhysicalThing[]>();
		for (const t of things) {
			const rx = t.xy.x - minX;
			const ry = t.xy.y - minY;
			const key = cellKey(rx, ry);
			let list = cellMap.get(key);
			if (!list) {
				list = [];
				cellMap.set(key, list);
			}
			list.push(t);
		}
		// Sort each cell by z-order (lower first = earlier layer)
		for (const list of cellMap.values()) {
			list.sort((a, b) => a.getZOrder() - b.getZOrder());
		}

		// Determine layer count
		let maxThingsPerCell = 0;
		for (const list of cellMap.values()) {
			if (list.length > maxThingsPerCell) maxThingsPerCell = list.length;
		}
		const layerCount = Math.max(1, maxThingsPerCell);

		// Build class-to-character mapping using static key property
		const classNames = new Set<string>();
		for (const t of things) {
			classNames.add((t.constructor as any).key as string);
		}

		const classToChar = new Map<string, string>();
		const usedChars = new Set<string>();
		usedChars.add(" "); // space is reserved

		// Sort class names for deterministic assignment
		const sortedNames = [...classNames].sort();

		for (const name of sortedNames) {
			const category = CATEGORY_PREFS.find((cat) => cat.match(name));
			let assigned = false;

			// Try category preferred chars first
			if (category) {
				for (const ch of category.chars) {
					if (!usedChars.has(ch)) {
						classToChar.set(name, ch);
						usedChars.add(ch);
						assigned = true;
						break;
					}
				}
			}

			// Try first letter of key (skip reserved chars)
			if (!assigned) {
				const firstLetter = name[0];
				if (!usedChars.has(firstLetter) && !RESERVED_CHARS.has(firstLetter)) {
					classToChar.set(name, firstLetter);
					usedChars.add(firstLetter);
					assigned = true;
				}
			}

			// Fall back to general pool
			if (!assigned) {
				for (const ch of filteredPool) {
					if (!usedChars.has(ch)) {
						classToChar.set(name, ch);
						usedChars.add(ch);
						assigned = true;
						break;
					}
				}
			}

			if (!assigned) {
				throw new Error(`Ran out of legend characters while assigning '${name}'.`);
			}
		}

		// Build legend: character → class name
		const legend: Record<string, string> = {};
		for (const [name, char] of classToChar) {
			legend[char] = name;
		}

		// Build layout/layers
		const buildLayer = (map: Map<string, PhysicalThing[]>, layerIndex: number): string[] => {
			const rows: string[] = [];
			for (let y = 0; y < height; y++) {
				let row = "";
				for (let x = 0; x < width; x++) {
					const key = cellKey(x, y);
					const list = map.get(key);
					if (list && layerIndex < list.length) {
						const thing = list[layerIndex];
						const className = (thing.constructor as any).key as string;
						row += classToChar.get(className)!;
					} else {
						row += " ";
					}
				}
				rows.push(row);
			}
			return rows;
		};

		const result: PrefabData = { legend };

		// When multi-layer and floors share cells with non-floors,
		// separate into floor-only layers first, then non-floor layers.
		const shouldSeparateFloors =
			layerCount > 1 &&
			[...cellMap.values()].some((list) => {
				const hasFloor = list.some((t) => t instanceof Floor);
				const hasNonFloor = list.some((t) => !(t instanceof Floor));
				return hasFloor && hasNonFloor;
			});

		if (layerCount === 1) {
			result.layout = buildLayer(cellMap, 0);
		} else if (shouldSeparateFloors) {
			const floorMap = new Map<string, PhysicalThing[]>();
			const nonFloorMap = new Map<string, PhysicalThing[]>();
			for (const [key, list] of cellMap) {
				const floors = list.filter((t) => t instanceof Floor);
				const rest = list.filter((t) => !(t instanceof Floor));
				if (floors.length) floorMap.set(key, floors);
				if (rest.length) nonFloorMap.set(key, rest);
			}

			let maxFloors = 0;
			for (const list of floorMap.values()) if (list.length > maxFloors) maxFloors = list.length;
			let maxNonFloors = 0;
			for (const list of nonFloorMap.values()) if (list.length > maxNonFloors) maxNonFloors = list.length;

			result.layers = [];
			for (let i = 0; i < maxFloors; i++) result.layers.push(buildLayer(floorMap, i));
			for (let i = 0; i < maxNonFloors; i++) result.layers.push(buildLayer(nonFloorMap, i));
		} else {
			result.layers = [];
			for (let i = 0; i < layerCount; i++) {
				result.layers.push(buildLayer(cellMap, i));
			}
		}

		if (this.cornerRequired) result.cornerRequired = true;
		if (this.borderRequired) result.borderRequired = true;

		return result;
	}
}
